import { useEffect, useMemo, useState } from 'react';
import SubcontractorDialog from '../../subcontractors/components/SubcontractorDialog';
import { INITIAL_SUBCONTRACTOR_STATE } from '../../subcontractors/constants/subcontractorsConstants';
import { useCountries } from '../../../hooks/useCountries';
import { useProvinces } from '../../../hooks/useProvinces';
import { useDepartments } from '../../../hooks/useDepartments';
import { useDistricts } from '../../../hooks/useDistricts';
import { useCreateSubcontractor } from '../../../hooks/useSubcontractors';

const normalizeErrorMessage = (value) => {
  if (value === null || value === undefined) return '';

  if (typeof value === 'string') {
    return value.trim();
  }

  if (Array.isArray(value)) {
    const messages = value
      .map((item) => normalizeErrorMessage(item?.msg || item?.message || item))
      .filter(Boolean);
    return messages.join(' | ');
  }

  if (typeof value === 'object') {
    if (typeof value.message === 'string') return value.message.trim();
    if (typeof value.error === 'string') return value.error.trim();
    if (typeof value.detail === 'string') return value.detail.trim();
    try {
      return JSON.stringify(value);
    } catch (_error) {
      return String(value);
    }
  }

  return String(value);
};

const BeneficiaryDialog = ({ open, onClose, onCreated }) => {
  const { data: countries = [] } = useCountries();
  const { data: provinces = [] } = useProvinces();
  const { data: departments = [] } = useDepartments();
  const { data: districts = [] } = useDistricts();

  const [selectedSubcontractor, setSelectedSubcontractor] = useState(INITIAL_SUBCONTRACTOR_STATE);
  const [errorMessage, setErrorMessage] = useState('');
  const createMutation = useCreateSubcontractor();

  useEffect(() => {
    if (!open) {
      setSelectedSubcontractor(INITIAL_SUBCONTRACTOR_STATE);
      setErrorMessage('');
    }
  }, [open]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setSelectedSubcontractor((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (event) => {
    const { name, value } = event.target;
    setSelectedSubcontractor((prev) => ({ ...prev, [name]: value }));
  };

  const extractErrorMessage = (error) => {
    const candidates = [
      error?.response?.data?.message,
      error?.response?.data?.detail,
      error?.response?.data?.error,
      error?.message,
    ];

    for (const candidate of candidates) {
      const normalized = normalizeErrorMessage(candidate);
      if (normalized) {
        return normalized;
      }
    }

    return 'No se pudo crear el beneficiario';
  };

  const handleClose = () => {
    setErrorMessage('');
    onClose();
  };

  const handleSave = async () => {
    const nameValue =
      selectedSubcontractor.company?.trim() || selectedSubcontractor.legal_business_name?.trim();
    if (!nameValue) {
      setErrorMessage('El nombre es obligatorio');
      return;
    }

    try {
      setErrorMessage('');
      const created = await createMutation.mutateAsync(selectedSubcontractor);
      onCreated?.(created);
      setSelectedSubcontractor(INITIAL_SUBCONTRACTOR_STATE);
      handleClose();
    } catch (error) {
      console.error('Error al crear el beneficiario:', error);
      setErrorMessage(extractErrorMessage(error));
    }
  };

  const dialogActionsProps = useMemo(
    () => ({
      saveButtonProps: {
        isLoading: createMutation.isPending,
      },
    }),
    [createMutation.isPending]
  );

  return (
    <SubcontractorDialog
      open={open}
      onClose={handleClose}
      editMode={false}
      selectedSubcontractor={selectedSubcontractor}
      onInputChange={handleInputChange}
      onSelectChange={handleSelectChange}
      onSave={handleSave}
      countries={countries}
      provinces={provinces}
      departments={departments}
      districts={districts}
      dialogTitle="Nuevo beneficiario"
      errorMessage={errorMessage}
      dialogActionsProps={dialogActionsProps}
    />
  );
};

export default BeneficiaryDialog;
